import { resolveHealthDaypartTag } from '@/domain/health/overviewTypes';

interface HealthOverviewDateChange {
    previousCageId: number | undefined;
    cageId: number;
    fishGroupId: number | undefined;
    daypartTag: string | undefined;
    /**
     * Key of the answer being kept. `useHealthController_getHealthData` stores
     * `[name, cageId, queryParams]`, and the params carry the fish group and daypart.
     */
    previousQueryKey: readonly unknown[] | undefined;
}

/**
 * The previous overview may stand in while a new date range loads. A different pen, fish
 * group, or daypart must not: those score bars belong to another selection.
 */
export function isHealthOverviewDateChange(change: HealthOverviewDateChange): boolean {
    if (change.previousCageId !== change.cageId) {
        return false;
    }

    const scope = readHealthOverviewScope(change.previousQueryKey);
    if (scope == null) {
        return false;
    }

    return (
        scope.fishGroupId === change.fishGroupId &&
        scope.daypartTag === resolveHealthDaypartTag(change.daypartTag)
    );
}

function readHealthOverviewScope(
    queryKey: readonly unknown[] | undefined
): { fishGroupId: number | undefined; daypartTag: string } | undefined {
    const params = queryKey?.[2];
    if (!isRecord(params) || typeof params.daypartTag !== 'string') {
        return undefined;
    }
    if (params.fishGroupId != null && typeof params.fishGroupId !== 'number') {
        return undefined;
    }

    return {
        fishGroupId: typeof params.fishGroupId === 'number' ? params.fishGroupId : undefined,
        daypartTag: params.daypartTag,
    };
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}
